<template>
  <div>
    <Label class="text-sm font-medium">Effects</Label>
    <div class="mt-3 space-y-3">
      <!-- Opacity -->
      <InheritedField
        label="Opacity"
        :is-inherited="isInherited"
        :is-overridden="opacityOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.opacity')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs text-muted-foreground">{{ opacityValue }}%</span>
            </div>
            <Slider
              :model-value="[opacityValue]"
              :max="100"
              :min="0"
              :step="1"
              @update:model-value="updateOpacity"
              :class="{ 'opacity-100': showInheritedBorder }"
            />
          </div>
        </template>
      </InheritedField>

      <!-- Blend Mode -->
      <InheritedField
        label="Blend Mode"
        :is-inherited="isInherited"
        :is-overridden="blendModeOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.blendMode')"
      >
        <template #default="{ showInheritedBorder }">
          <Select :model-value="blendMode || 'normal'" @update:model-value="handleBlendModeUpdate">
            <SelectTrigger 
              class="h-8"
              :class="{ 'border-orange-400': showInheritedBorder }"
            >
              <SelectValue placeholder="Select blend mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="multiply">Multiply</SelectItem>
              <SelectItem value="screen">Screen</SelectItem>
              <SelectItem value="overlay">Overlay</SelectItem>
              <SelectItem value="darken">Darken</SelectItem>
              <SelectItem value="lighten">Lighten</SelectItem>
              <SelectItem value="color-dodge">Color Dodge</SelectItem>
              <SelectItem value="color-burn">Color Burn</SelectItem>
              <SelectItem value="hard-light">Hard Light</SelectItem>
              <SelectItem value="soft-light">Soft Light</SelectItem>
              <SelectItem value="difference">Difference</SelectItem>
              <SelectItem value="exclusion">Exclusion</SelectItem>
              <SelectItem value="hue">Hue</SelectItem>
              <SelectItem value="saturation">Saturation</SelectItem>
              <SelectItem value="color">Color</SelectItem>
              <SelectItem value="luminosity">Luminosity</SelectItem>
            </SelectContent>
          </Select>
        </template>
      </InheritedField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import InheritedField from './InheritedField.vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Props {
  opacity?: number
  blendMode?: string
  isInherited?: boolean
  opacityOverridden?: boolean
  blendModeOverridden?: boolean
  parentSceneName?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  updateOpacity: [value: number]
  updateBlendMode: [value: string]
  resetField: [fieldPath: string]
}>()

const opacityValue = computed(() => Math.round((props.opacity ?? 1) * 100))

const updateOpacity = (value: number[] | undefined) => {
  if (value) {
    emit('updateOpacity', value[0] / 100)
  }
}

const handleBlendModeUpdate = (value: any) => {
  if (value && typeof value === 'string') {
    emit('updateBlendMode', value)
  }
}
</script>
